import { useEffect } from "react";

// Third party
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Subscriptions
import {
	onCreateSaleSpiffAward,
	onCreateServiceSpiffAward,
	onDeleteSaleSpiffAward,
	onDeleteServiceSpiffAward,
	onUpdateSaleSpiffAward,
	onUpdateServiceSpiffAward,
} from "@/Motivators/api/awards/subscriptions";

// Types
import { GenericTree, TreeNode } from "@/Common/GenericTree/types";
import { getLeafKey, removeTreeNodes, updateTreeNode, upsertTreeNode } from "@/Common/GenericTree/utils";
import {
	onCreateSpiffItemDepartment,
	onDeleteSpiffItemsDepartment,
	onUpdateSpiffItemDepartment,
} from "@/Motivators/api/spiffs/subscriptions";
import { cloneDeep, every } from "lodash";
import { TSpiffAward } from "../types/awards";

interface IUseSpiffAwardSubscriptionsProps {
	locationId: string;
	commissionCycleId: string;
	spiffItemId: string;
	department: "sales" | "service";
	clearSelection: () => void;
	onSpiffUpdated: (updatedItem: any) => void;
}

interface SpiffItemSubscriptionResponse {
	_id: string;
	title: string;
	commissionCycle?: {
		_id: string;
		title: string;
		startDate: string;
	};
	items?: {
		_id: string;
		title: string;
		awardsCount: number;
	}[];
}

const useSpiffAwardSubscriptions = (props: IUseSpiffAwardSubscriptionsProps) => {
	const { locationId, commissionCycleId, spiffItemId, department, clearSelection, onSpiffUpdated } = props;

	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();

	const isSales = department === "sales";
	const typeName = isSales ? "SaleSpiffAward" : "ServiceSpiffAward";
	const sidebarQueryKey = [isSales ? "spiffAwardsSidebar" : "spiffBonusAwardsSidebar", locationId];
	const awardsQueryKey = [`${department}SpiffAwards`, locationId, spiffItemId];

	const onCreateAwardSubscription = isSales ? onCreateSaleSpiffAward : onCreateServiceSpiffAward;
	const onUpdateAwardSubscription = isSales ? onUpdateSaleSpiffAward : onUpdateServiceSpiffAward;
	const onDeleteAwardSubscription = isSales ? onDeleteSaleSpiffAward : onDeleteServiceSpiffAward;

	const updateTreeNodeCount = (params: { leafKey: string; delta: number }) => {
		const { leafKey, delta } = params;

		queryClient.setQueryData<GenericTree | undefined>(sidebarQueryKey, (oldTree) => {
			if (!oldTree?.tree?.length) return oldTree;

			const { nodes, changed } = updateTreeNode(
				oldTree.tree,
				(node) => node._id === leafKey,
				(node) => {
					const currentCount = Number(node.itemCount ?? 0);
					const nextCount = Math.max(0, currentCount + delta);
					return { ...node, itemCount: String(nextCount) };
				}
			);

			if (!changed) return oldTree;

			return {
				...oldTree,
				tree: nodes ?? oldTree.tree,
			};
		});
	};

	const createAwardSub = getSubscription([`create${typeName}`, locationId, commissionCycleId]);
	const updateAwardSub = getSubscription([`update${typeName}`, locationId, commissionCycleId]);
	const deleteAwardSub = getSubscription([`delete${typeName}`, locationId, commissionCycleId]);

	createAwardSub?.subscriptions?.[`onCreate${typeName}`]?.setCallback((result: { data: Record<string, TSpiffAward> }) => {
		const response = result.data[`onCreate${typeName}`];
		const spiffItemId = response?.spiffItem?._id;
		if (!spiffItemId) return;

		const leafKey = getLeafKey([response.commissionCycleId, response.spiffSetup?._id, spiffItemId]);

		queryClient.setQueryData(awardsQueryKey, (old: TSpiffAward[] = []) => {
			const existingIndex = old.findIndex((item) => item.recipient._id === response.recipient._id);
			if (existingIndex >= 0) {
				const updated = [...old];
				updated[existingIndex] = {
					...updated[existingIndex],
					awards: [...response.awards],
				};
				return updated;
			}

			return [...old, response];
		});

		updateTreeNodeCount({ leafKey, delta: 1 });
	});

	updateAwardSub?.subscriptions?.[`onUpdate${typeName}`]?.setCallback((result: { data: Record<string, TSpiffAward> }) => {
		const response = result.data[`onUpdate${typeName}`];
		const spiffItemId = response?.spiffItem?._id;
		if (!spiffItemId) return;

		queryClient.setQueryData(awardsQueryKey, (old: TSpiffAward[] = []) => {
			return old.map((item) => {
				if (item.recipient._id === response.recipient._id) {
					return {
						...item,
						awards: item.awards.map((award) => {
							const updatedAward = response.awards.find((newAward) => newAward._id === award._id);
							return updatedAward || award;
						}),
					};
				}
				return item;
			});
		});
	});

	deleteAwardSub?.subscriptions?.[`onDelete${typeName}`]?.setCallback((result: { data: Record<string, TSpiffAward> }) => {
		const response = result.data[`onDelete${typeName}`];
		const spiffItemId = response?.spiffItem?._id;
		if (!spiffItemId) return;

		const leafKey = getLeafKey([response.commissionCycleId, response.spiffSetup?._id, spiffItemId]);
		const deletedIds = new Set((response.awards ?? []).map((award) => award._id));
		const deletedCount = response.awards?.length ?? 0;

		queryClient.setQueryData(awardsQueryKey, (old: TSpiffAward[] = []) => {
			const updated = old
				.map((item) => ({
					...item,
					awards: item.awards.filter((award) => !deletedIds.has(award._id)),
				}))
				.filter((item) => item.awards.length > 0); // Remove recipients with no awards

			return updated;
		});

		updateTreeNodeCount({ leafKey, delta: -deletedCount });
	});

	useEffect(() => {
		if (!every([locationId, commissionCycleId, spiffItemId, department], Boolean)) {
			return;
		}

		addSubscription(
			[`create${typeName}`, locationId, commissionCycleId],
			[
				{
					context: `onCreate${typeName}`,
					query: onCreateAwardSubscription,
					variables: {
						locationId,
						commissionCycleId,
					},
				},
			]
		);

		addSubscription(
			[`update${typeName}`, locationId, commissionCycleId],
			[
				{
					context: `onUpdate${typeName}`,
					query: onUpdateAwardSubscription,
					variables: {
						locationId,
						commissionCycleId,
					},
				},
			]
		);

		addSubscription(
			[`delete${typeName}`, locationId, commissionCycleId],
			[
				{
					context: `onDelete${typeName}`,
					query: onDeleteAwardSubscription,
					variables: {
						locationId,
						commissionCycleId,
					},
				},
			]
		);
	}, [locationId, commissionCycleId, spiffItemId, department]);

	const createSpiffItemSub = getSubscription([`createSpiffItem`, locationId, department]);
	const updateSpiffItemSub = getSubscription([`updateSpiffItem`, locationId, department]);
	const deleteSpiffItemsSub = getSubscription([`deleteSpiffItems`, locationId, department]);

	createSpiffItemSub?.subscriptions?.["onCreateSpiffItem"]?.setCallback(
		(result: { data: { onCreateSpiffItemDepartment: SpiffItemSubscriptionResponse } }) => {
			const response = result.data.onCreateSpiffItemDepartment;
			if (!response?.commissionCycle || !response.items?.length) return;

			const { _id: setupId, title: setupName } = response;
			const { _id: cycleId, title: cycleName, startDate: cycleStartDate } = response.commissionCycle;

			const year = moment.utc(cycleStartDate).format("YYYY");

			queryClient.setQueryData<GenericTree | undefined>(sidebarQueryKey, (oldTree) => {
				if (!oldTree) return oldTree;
				const newTree = cloneDeep(oldTree.tree);

				const { node: yearNode } = upsertTreeNode(
					newTree,
					year,
					() => ({ _id: year, title: year, bold: true, list: [] }),
					(a, b) => parseInt(a._id) - parseInt(b._id)
				);

				if (!yearNode.list) yearNode.list = [];

				const { node: cycleNode } = upsertTreeNode(
					yearNode.list,
					cycleId,
					() => ({ _id: cycleId, title: cycleName, startDate: cycleStartDate, list: [] }) as TreeNode,
					(a, b) => ((a as any).startDate ?? "").localeCompare((b as any).startDate ?? "")
				);

				if (!cycleNode.list) cycleNode.list = [];

				const { node: setupNode } = upsertTreeNode(cycleNode.list, setupId, () => ({
					_id: setupId,
					title: setupName,
					list: [],
				}));

				if (!setupNode.list) setupNode.list = [];

				response.items?.forEach((item) => {
					const compositeItemId = getLeafKey([cycleId, setupId, item._id]);
					const existingItem = setupNode.list!.find((n) => n._id === compositeItemId);
					if (!existingItem) {
						setupNode.list!.push({
							_id: compositeItemId,
							title: item.title,
							itemCount: "0",
						});
					}
				});

				return { ...oldTree, tree: newTree };
			});

			queryClient.invalidateQueries({ queryKey: [`${department}SpiffSetup`, locationId, setupId] });
		}
	);

	updateSpiffItemSub?.subscriptions?.["onUpdateSpiffItem"]?.setCallback(
		(result: { data: { onUpdateSpiffItemDepartment: SpiffItemSubscriptionResponse } }) => {
			const response = result.data.onUpdateSpiffItemDepartment;
			if (!response?.commissionCycle?._id || !response.items?.length) return;

			const cycleId = response.commissionCycle._id;
			const setupId = response._id;
			const [spiffItem] = response.items;

			onSpiffUpdated(spiffItem);

			queryClient.setQueryData<GenericTree | undefined>(sidebarQueryKey, (oldTree) => {
				if (!oldTree?.tree?.length) return oldTree;

				let treeChanged = false;
				let newTree = cloneDeep(oldTree.tree);

				response.items?.forEach((item) => {
					const compositeItemId = getLeafKey([cycleId, setupId, item._id]);

					const { nodes, changed } = updateTreeNode(
						newTree,
						(node) => node._id === compositeItemId,
						(node) => ({ ...node, title: item.title, itemCount: String(spiffItem.awardsCount) })
					);

					if (changed) {
						treeChanged = true;
						newTree = nodes ?? newTree;
					}
				});

				if (!treeChanged) return oldTree;

				return { ...oldTree, tree: newTree };
			});

			queryClient.invalidateQueries({ queryKey: awardsQueryKey });
		}
	);

	deleteSpiffItemsSub?.subscriptions?.["onDeleteSpiffItems"]?.setCallback(
		(result: { data: { onDeleteSpiffItemsDepartment: SpiffItemSubscriptionResponse } }) => {
			const response = result.data.onDeleteSpiffItemsDepartment;
			if (!response?.commissionCycle?._id || !response.items?.length) return;

			const cycleId = response.commissionCycle._id;
			const setupId = response._id;

			const itemsToRemove = response.items.map((item) => getLeafKey([cycleId, setupId, item._id]));

			if (!itemsToRemove?.length) return;

			queryClient.setQueryData<GenericTree | undefined>(sidebarQueryKey, (oldTree) => {
				if (!oldTree?.tree?.length) return oldTree;

				const { nodes: newTree, changed } = removeTreeNodes(oldTree.tree, itemsToRemove);
				if (!changed) return oldTree;
				const cleanedTree = newTree.filter((yearNode) => yearNode.list && yearNode.list.length > 0);

				return { ...oldTree, tree: cleanedTree };
			});

			queryClient.invalidateQueries({ queryKey: [`${department}SpiffSetup`, locationId, setupId] });
			clearSelection();
		}
	);

	useEffect(() => {
		if (!every([locationId, department], Boolean)) {
			return;
		}

		addSubscription(
			[`createSpiffItem`, locationId, department],
			[
				{
					context: `onCreateSpiffItem`,
					query: onCreateSpiffItemDepartment,
					variables: {
						locationId,
						department,
					},
				},
			]
		);

		addSubscription(
			[`updateSpiffItem`, locationId, department],
			[
				{
					context: `onUpdateSpiffItem`,
					query: onUpdateSpiffItemDepartment,
					variables: {
						locationId,
						department,
					},
				},
			]
		);

		addSubscription(
			[`deleteSpiffItems`, locationId, department],
			[
				{
					context: `onDeleteSpiffItems`,
					query: onDeleteSpiffItemsDepartment,
					variables: {
						locationId,
						department,
					},
				},
			]
		);
	}, [locationId, department]);

	return null;
};

export default useSpiffAwardSubscriptions;
